"use client";

import { usePathname, useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarItem,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSectionRule,
  SidebarShortcut,
} from "@/components/layout/sidebar/sidebar-layout";
import { NotificationsSidebarItem } from "@/components/layout/sidebar/notifications-sidebar-item";
import { SidebarConversationsItem } from "@/components/layout/sidebar/conversations-item";
import { ProjectSelect } from "@/components/layout/navbar/project-select";
import { SidebarSearchesSection } from "@/components/layout/sidebar/searches-section";
import { useSidebar } from "@/stores/sidebar-store";
import { useCommandMenuStore } from "@/stores/command-menu-store";
import { useAuth } from "@/features/auth";
import useIsMobile from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";
import { getJob, useGetTaskCounts } from "@/lib/api/generated";
import { matchJobRoute } from "@/features/jobs/components/view/layout/job-tab-items";
import { matchSearchRoute } from "@/components/search/search-tabs";
import { Icon } from "@/components/ui/icon";
import { useProjectSequencesSheet } from "@/hooks/use-project-sequences-sheet";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";
import { useConversationsFeature } from "@/features/conversations/hooks/use-conversations-feature";
import { COPY } from "@/features/conversations/lib/copy";

function SidebarDrillRow({
  label,
  onDrillIn,
  className,
}: {
  label: string;
  onDrillIn: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5 pt-2.5", className)}>
      <button
        type="button"
        onClick={onDrillIn}
        className="flex h-8 w-fit cursor-pointer items-center gap-1.5 rounded-md px-2 text-muted-foreground"
      >
        <span className="min-w-0 flex-1 truncate text-left text-sm font-medium leading-5">
          {label}
        </span>
        <Icon
          name="IconChevronRight"
          className="shrink-0 size-3.5 text-muted-foreground"
        />
      </button>
    </div>
  );
}

export function SidebarMainContent({
  onSettingsDrillIn,
  onJobDrillIn,
  onSearchDrillIn,
  onConversationsDrillIn,
}: {
  onSettingsDrillIn?: () => void;
  onJobDrillIn?: () => void;
  onSearchDrillIn?: () => void;
  onConversationsDrillIn?: () => void;
}) {
  const { expanded, setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const sidebarExpanded = isMobile ? true : expanded;
  const pathname = usePathname();
  const router = useRouter();
  const { selectedProjectId, isLoading: authLoading } = useAuth();
  const requireProjectForJobsAndSearch = !authLoading && !selectedProjectId;
  const openCommandMenu = useCommandMenuStore((s) => s.setOpen);

  // On a job view tab route a contextual "«job name» ›" drill row renders
  // above the menu (mobile-only, threaded via `onJobDrillIn`) — a shortcut
  // into the on-page job tab strip's mobile equivalent (`JobPanel`). The
  // Organize "Jobs" row stays a plain `/jobs` link regardless.
  const jobMatch = matchJobRoute(pathname);
  const showJobDrillRow = !!onJobDrillIn && !!jobMatch;
  // Same query key/fn as the job detail layout (`["job", jobId]` +
  // `getJob`), so this is a cache hit — no extra fetch on drawer open.
  const jobQuery = useQuery({
    queryKey: ["job", jobMatch?.jobId ?? ""],
    queryFn: () => getJob(jobMatch!.jobId),
    enabled: showJobDrillRow,
  });
  const jobDrillLabel = jobQuery.data?.title ?? "Job";
  // Same idea for "Search" (mobile-only, threaded via `onSearchDrillIn`):
  // shown on any `/talent-search` route as a shortcut into the on-page
  // Search/Arbitrator/Insights tabs' mobile equivalent.
  const onSearchRoute = !!matchSearchRoute(pathname);
  const { openProjectSequences } = useProjectSequencesSheet();

  const taskCounts = useGetTaskCounts(
    { project_id: selectedProjectId ?? "" },
    { query: { enabled: !!selectedProjectId } },
  );
  const hasUnreadTasks = (taskCounts.data?.unread_for_user ?? 0) > 0;

  const sectionPadding = cn("px-3.5", !sidebarExpanded && "items-center");
  // Only a specific sequence detail (…/sequences/:id) activates Sequences;
  // the job sequences list stays under Jobs.
  const isOnSequenceDetail =
    /\/jobs\/[^/]+\/sequences\/[^/]+/.test(pathname);
  const isOnJobs =
    (pathname === "/jobs" || pathname.startsWith("/jobs/")) &&
    !isOnSequenceDetail;
  const conversationsEnabled = useConversationsFeature();
  const isOnConversations =
    pathname === "/conversations" || pathname.startsWith("/conversations/");
  const showConversationsDrillRow =
    !!onConversationsDrillIn && isOnConversations && conversationsEnabled;

  // The mobile drawer has no footer; Settings / Docs / Support live in the
  // scrolling menu there instead (Settings drills into its own panel).
  const showResourcesGroup = !!onSettingsDrillIn;

  return (
    <>
      {showJobDrillRow && onJobDrillIn && (
        <SidebarDrillRow
          label={jobDrillLabel}
          onDrillIn={onJobDrillIn}
          className={sectionPadding}
        />
      )}
      {showConversationsDrillRow && onConversationsDrillIn && (
        <SidebarDrillRow
          label={COPY.area}
          onDrillIn={onConversationsDrillIn}
          className={sectionPadding}
        />
      )}

      {/* Global actions */}
      <SidebarGroup className="shrink-0 py-2.5">
        <SidebarGroupContent className={sectionPadding}>
          <SidebarMenu>
            {sidebarExpanded ? (
              /* Quick find box + bell share one row when there's room. */
              <SidebarMenuItem>
                <div className="flex items-center gap-1.5 pb-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      if (isMobile) setOpenMobile(false);
                      openCommandMenu(true);
                    }}
                    className={cn(
                      "group/quick flex h-8 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md pl-2 pr-2",
                      "bg-contrast/[0.03] shadow-[inset_0_0_0_1px_var(--border-alpha)]",
                      "transition-[background-color,box-shadow] duration-150",
                      "hover:bg-contrast/[0.06] hover:shadow-[inset_0_0_0_1px_var(--border-strong-alpha,var(--border-alpha))]",
                      "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
                    )}
                  >
                    <Icon
                      name="IconMagnifyingGlass"
                      size={14}
                      className="shrink-0 text-contrast/40 transition-colors group-hover/quick:text-heading"
                    />
                    <span className="min-w-0 flex-1 truncate text-left text-[13px] leading-5 text-contrast/50 transition-colors group-hover/quick:text-heading">
                      Quick find
                    </span>
                    {/* Keyboard hint is noise on the touch drawer. */}
                    {!isMobile && <SidebarShortcut keys={["mod", "K"]} />}
                  </button>
                  <NotificationsSidebarItem expanded compact />
                </div>
              </SidebarMenuItem>
            ) : (
              <>
                <SidebarMenuItem>
                  <SidebarItem
                    icon="IconMagnifyingGlass"
                    label="Search"
                    shortcut={["mod", "K"]}
                    expanded={false}
                    onClick={() => openCommandMenu(true)}
                  />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <NotificationsSidebarItem expanded={false} />
                </SidebarMenuItem>
              </>
            )}
            {/* Project switcher; collapses to its cover tile on the rail.
                Organizations are switched from the account menu. */}
            <SidebarMenuItem>
              <ProjectSelect
                appearance="sidebar"
                collapsed={!sidebarExpanded}
                className={sidebarExpanded ? "min-w-0" : undefined}
              />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* Workspace */}
      <SidebarGroup className="shrink-0 pb-2.5">
        <SidebarGroupContent className={sectionPadding}>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarItem
                href="/projects"
                icon="IconFolderBookmarks"
                label="Projects"
                expanded={sidebarExpanded}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                href="/tasks"
                icon="IconSquareChecklist"
                label="Tasks"
                dot={hasUnreadTasks}
                onDotClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  router.push("/tasks?read=unread");
                }}
                expanded={sidebarExpanded}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                href="/jobs"
                icon="IconBag"
                label="Jobs"
                expanded={sidebarExpanded}
                active={isOnJobs}
                disabled={requireProjectForJobsAndSearch}
                disabledReason="Select a project to access Jobs"
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarConversationsItem
                expanded={sidebarExpanded}
                projectId={selectedProjectId ?? null}
                active={isOnConversations}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                icon="IconPaperPlane"
                label="Sequences"
                expanded={sidebarExpanded}
                active={isOnSequenceDetail}
                disabled={requireProjectForJobsAndSearch}
                disabledReason="Select a project to access Sequences"
                onClick={openProjectSequences}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                href="/presets"
                icon="IconLayoutDashboard"
                label="Presets"
                expanded={sidebarExpanded}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                href="/snippets"
                icon="IconScissors2"
                label="Snippets"
                expanded={sidebarExpanded}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarItem
                href="/network"
                icon="IconHandshake"
                label="Team network"
                expanded={sidebarExpanded}
              />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarSectionRule expanded={sidebarExpanded} />

      {/* Searches | Agents (locked). On desktop this section owns the only
          scroller, so everything above it stays put. */}
      <SidebarSearchesSection
        expanded={sidebarExpanded}
        disabled={requireProjectForJobsAndSearch}
        disabledReason="Select a project to access Talent Search"
        fill={!isMobile}
      />

      {showResourcesGroup && (
        <>
          <SidebarSectionRule expanded={sidebarExpanded} />
          <SidebarGroup className="py-2.5">
            <SidebarGroupContent className={sectionPadding}>
              <SidebarMenu>
                {onSearchDrillIn && onSearchRoute && (
                  <SidebarMenuItem>
                    <SidebarItem
                      icon="IconSearchIntelligence"
                      label="Search"
                      onClick={onSearchDrillIn}
                      active={onSearchRoute}
                      chevron
                      expanded={sidebarExpanded}
                    />
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarItem
                    icon="IconSettingsGear2"
                    label="Settings"
                    onClick={onSettingsDrillIn}
                    active={pathname.startsWith("/settings")}
                    chevron
                    expanded={sidebarExpanded}
                  />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarItem
                    href={NEUROSCALE_LINKS.docs}
                    icon="IconGraduateCap"
                    label="Documentation"
                    external
                    expanded={sidebarExpanded}
                  />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarItem
                    href="mailto:support@arbi.ai"
                    icon="IconRescueRing"
                    label="Contact Support"
                    external
                    expanded={sidebarExpanded}
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      )}
    </>
  );
}
